if (!customElements.get('overflow-scrollbar')) {
  customElements.define(
    'overflow-scrollbar',
    class OverflowScrollbar extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('[data-overflow-scrollbar-content]');
        this.track = this.querySelector('[data-overflow-scrollbar-track]');
        this.thumb = this.querySelector('[data-overflow-scrollbar-thumb]');

        if (!this.content || !this.track || !this.thumb) return;

        this.onScrollHandler = this.updateScrollbar.bind(this);
        this.onResizeHandler = this.requestScrollbarUpdate.bind(this);
        this.onMutationHandler = this.requestScrollbarUpdate.bind(this);
        this.onImageLoadHandler = this.requestScrollbarUpdate.bind(this);
        this.onContentPointerDownHandler = this.onContentPointerDown.bind(this);
        this.onContentPointerMoveHandler = this.onContentPointerMove.bind(this);
        this.onContentPointerUpHandler = this.onContentPointerUp.bind(this);
        this.onTrackPointerDownHandler = this.onTrackPointerDown.bind(this);
        this.onThumbPointerDownHandler = this.onThumbPointerDown.bind(this);
        this.onThumbPointerMoveHandler = this.onThumbPointerMove.bind(this);
        this.onThumbPointerUpHandler = this.onThumbPointerUp.bind(this);
        this.onWindowLoadHandler = this.requestScrollbarUpdate.bind(this);

        this.content.addEventListener('scroll', this.onScrollHandler, {
          passive: true
        });
        this.content.addEventListener('pointerdown', this.onContentPointerDownHandler);
        this.track.addEventListener('pointerdown', this.onTrackPointerDownHandler);
        this.thumb.addEventListener('pointerdown', this.onThumbPointerDownHandler);

        this.resizeObserver = new ResizeObserver(this.onResizeHandler);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.content);
        this.resizeObserver.observe(this.track);
        if (this.content.firstElementChild) {
          this.resizeObserver.observe(this.content.firstElementChild);
        }

        this.mutationObserver = new MutationObserver(this.onMutationHandler);
        this.mutationObserver.observe(this.content, {
          childList: true,
          subtree: true
        });

        this.content.querySelectorAll('img').forEach((image) => {
          if (image.complete) return;
          image.addEventListener('load', this.onImageLoadHandler, { once: true });
        });
        window.addEventListener('load', this.onWindowLoadHandler, { once: true });

        this.requestScrollbarUpdate();
      }

      disconnectedCallback() {
        this.content?.removeEventListener('scroll', this.onScrollHandler);
        this.content?.removeEventListener('pointerdown', this.onContentPointerDownHandler);
        this.content?.removeEventListener('pointermove', this.onContentPointerMoveHandler);
        this.content?.removeEventListener('pointerup', this.onContentPointerUpHandler);
        this.content?.removeEventListener('pointercancel', this.onContentPointerUpHandler);
        this.track?.removeEventListener('pointerdown', this.onTrackPointerDownHandler);
        this.thumb?.removeEventListener('pointerdown', this.onThumbPointerDownHandler);
        this.thumb?.removeEventListener('pointermove', this.onThumbPointerMoveHandler);
        this.thumb?.removeEventListener('pointerup', this.onThumbPointerUpHandler);
        this.thumb?.removeEventListener('pointercancel', this.onThumbPointerUpHandler);
        window.removeEventListener('load', this.onWindowLoadHandler);
        cancelAnimationFrame(this.updateFrame);
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.content?.querySelectorAll('img').forEach((image) => {
          image.removeEventListener('load', this.onImageLoadHandler);
        });
      }

      onContentPointerDown(event) {
        if (event.pointerType === 'touch' || event.button !== 0 || this.track.hidden) return;
        if (this.hasInteractiveTarget(event.target)) return;

        this.contentDragStartX = event.clientX;
        this.contentDragStartScrollLeft = this.content.scrollLeft;
        this.classList.add('overflow-scrollbar--dragging');
        this.content.setPointerCapture(event.pointerId);
        this.content.addEventListener('pointermove', this.onContentPointerMoveHandler);
        this.content.addEventListener('pointerup', this.onContentPointerUpHandler);
        this.content.addEventListener('pointercancel', this.onContentPointerUpHandler);
      }

      onContentPointerMove(event) {
        if (typeof this.contentDragStartX !== 'number') return;
        const deltaX = event.clientX - this.contentDragStartX;
        this.content.scrollLeft = this.contentDragStartScrollLeft - deltaX;
      }

      onContentPointerUp(event) {
        this.contentDragStartX = null;
        this.contentDragStartScrollLeft = null;
        this.classList.remove('overflow-scrollbar--dragging');
        this.content.releasePointerCapture?.(event.pointerId);
        this.content.removeEventListener('pointermove', this.onContentPointerMoveHandler);
        this.content.removeEventListener('pointerup', this.onContentPointerUpHandler);
        this.content.removeEventListener('pointercancel', this.onContentPointerUpHandler);
      }

      onTrackPointerDown(event) {
        if (event.target === this.thumb || this.track.hidden) return;

        const trackRect = this.track.getBoundingClientRect();
        const thumbWidth = this.thumb.offsetWidth;
        const targetOffset = event.clientX - trackRect.left - thumbWidth / 2;

        this.scrollToThumbOffset(targetOffset);
      }

      onThumbPointerDown(event) {
        if (event.pointerType === 'touch' || event.button !== 0 || this.track.hidden) return;

        event.preventDefault();
        event.stopPropagation();
        this.thumbDragStartX = event.clientX;
        this.thumbDragStartScrollLeft = this.content.scrollLeft;
        this.classList.add('overflow-scrollbar--dragging');
        this.thumb.setPointerCapture(event.pointerId);
        this.thumb.addEventListener('pointermove', this.onThumbPointerMoveHandler);
        this.thumb.addEventListener('pointerup', this.onThumbPointerUpHandler);
        this.thumb.addEventListener('pointercancel', this.onThumbPointerUpHandler);
      }

      onThumbPointerMove(event) {
        if (typeof this.thumbDragStartX !== 'number') return;

        const trackWidth = this.track.clientWidth;
        const thumbWidth = this.thumb.offsetWidth;
        const maxThumbOffset = trackWidth - thumbWidth;
        const maxScroll = this.content.scrollWidth - this.content.clientWidth;

        if (maxThumbOffset <= 0 || maxScroll <= 0) return;

        const deltaX = event.clientX - this.thumbDragStartX;
        const scrollDelta = (deltaX / maxThumbOffset) * maxScroll;

        this.content.scrollLeft = this.thumbDragStartScrollLeft + scrollDelta;
      }

      onThumbPointerUp(event) {
        this.thumbDragStartX = null;
        this.thumbDragStartScrollLeft = null;
        this.classList.remove('overflow-scrollbar--dragging');
        this.thumb.releasePointerCapture?.(event.pointerId);
        this.thumb.removeEventListener('pointermove', this.onThumbPointerMoveHandler);
        this.thumb.removeEventListener('pointerup', this.onThumbPointerUpHandler);
        this.thumb.removeEventListener('pointercancel', this.onThumbPointerUpHandler);
      }

      scrollToThumbOffset(offset) {
        const trackWidth = this.track.clientWidth;
        const thumbWidth = this.thumb.offsetWidth;
        const maxThumbOffset = trackWidth - thumbWidth;
        const maxScroll = this.content.scrollWidth - this.content.clientWidth;

        if (maxThumbOffset <= 0 || maxScroll <= 0) return;

        const clampedOffset = Math.min(Math.max(offset, 0), maxThumbOffset);
        this.content.scrollLeft = (clampedOffset / maxThumbOffset) * maxScroll;
      }

      requestScrollbarUpdate() {
        cancelAnimationFrame(this.updateFrame);
        this.updateFrame = requestAnimationFrame(() => {
          this.updateScrollbar();
        });
      }

      getCssLengthInPixels(value, fallback) {
        const numericValue = Number.parseFloat(value);
        if (Number.isNaN(numericValue)) return fallback;
        if (value.trim().endsWith('rem')) {
          const rootFontSize = Number.parseFloat(
            window.getComputedStyle(document.documentElement).fontSize
          );
          return numericValue * rootFontSize;
        }
        return numericValue;
      }

      hasInteractiveTarget(target) {
        return Boolean(
          target.closest(
            'a, button, input, select, textarea, label, summary, [role="button"], [contenteditable="true"], [data-overflow-scrollbar-ignore]'
          )
        );
      }

      updateScrollbar() {
        const maxScroll = this.content.scrollWidth - this.content.clientWidth;
        const hasOverflow = maxScroll > 0;

        this.track.hidden = !hasOverflow;
        this.classList.toggle('overflow-scrollbar--active', hasOverflow);

        if (!hasOverflow) {
          this.thumb.style.width = '100%';
          this.thumb.style.transform = 'translateX(0)';
          return;
        }

        const trackWidth = this.track.clientWidth;
        if (trackWidth <= 0) {
          this.thumb.style.width = '100%';
          this.thumb.style.transform = 'translateX(0)';
          return;
        }

        const minThumbWidth = this.getCssLengthInPixels(
          window.getComputedStyle(this).getPropertyValue('--overflow-scrollbar-min-thumb-width'),
          4 * Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize)
        );
        const thumbWidth = Math.min(
          trackWidth,
          Math.max(
            (this.content.clientWidth / this.content.scrollWidth) * trackWidth,
            minThumbWidth
          )
        );
        const progress = Math.min(1, Math.max(0, this.content.scrollLeft / maxScroll));
        const thumbOffset = (trackWidth - thumbWidth) * progress;

        this.thumb.style.width = `${thumbWidth}px`;
        this.thumb.style.transform = `translateX(${thumbOffset}px)`;
      }
    }
  );
}
